/**
 * Guest ("Try Gift Gaff") sessions — Firebase Anonymous Auth.
 *
 * A guest is a real Firebase user with a uid, so every Firestore path, hook, and
 * security rule in the app works unchanged. Creating an account later *links* a
 * credential to that same uid (see `PhoneAuth` mode="link"), so trial giftees are
 * retained without copying anything.
 *
 * Copying is only needed when a guest signs into a *different*, pre-existing
 * account: that swaps the Firebase user, so the tree is read while still
 * anonymous and re-written under the new uid. The abandoned anonymous user and
 * its documents are removed by the `cleanupAbandonedGuests` scheduled function —
 * the client cannot delete them after switching users, since the rules scope
 * writes to the signed-in uid.
 */

import { signInAnonymously, signOut } from "firebase/auth";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { auth, db } from "../../firebase.js";
import { ONBOARDING_STATUS_NEEDS_PROFILE } from "./userOnboardingStatus.js";

/** Per-giftee subcollections the app writes; copied with the giftee on migration. */
const GIFTEE_SUBCOLLECTIONS = ["reminders", "savedRecommendations"];

/** True for a trial visitor who has not created an account yet. */
export function isGuestUser(user) {
  return Boolean(user?.isAnonymous);
}

/**
 * Signs in anonymously and seeds `users/{uid}` so the trial shows up in
 * analytics and the cleanup sweep can be reasoned about from Firestore too.
 */
export async function startGuestSession() {
  const { user } = await signInAnonymously(auth);

  await setDoc(
    doc(db, "users", user.uid),
    {
      isGuest: true,
      guestStartedAt: serverTimestamp(),
      phoneVerified: false,
      onboardingStatus: ONBOARDING_STATUS_NEEDS_PROFILE,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    },
    { merge: true },
  );

  return user;
}

/** Reads every giftee (plus its subcollections) for `uid` into a plain object. */
export async function readGifteeTree(uid) {
  const gifteesSnap = await getDocs(collection(db, "users", uid, "giftees"));

  const giftees = [];
  for (const gifteeDoc of gifteesSnap.docs) {
    const subcollections = {};
    for (const name of GIFTEE_SUBCOLLECTIONS) {
      const snap = await getDocs(
        collection(db, "users", uid, "giftees", gifteeDoc.id, name),
      );
      subcollections[name] = snap.docs.map((d) => d.data());
    }
    giftees.push({ data: gifteeDoc.data(), subcollections });
  }

  return { giftees };
}

/**
 * Re-creates a tree read by `readGifteeTree` under `uid` with fresh document ids.
 * `userId` is rewritten because the reminders collection-group rules match it
 * against the signed-in uid.
 */
export async function writeGifteeTreeForUser(uid, tree) {
  const giftees = tree?.giftees ?? [];

  for (const giftee of giftees) {
    const gifteeRef = await addDoc(collection(db, "users", uid, "giftees"), {
      ...giftee.data,
      userId: uid,
      updatedAt: serverTimestamp(),
    });

    for (const [name, rows] of Object.entries(giftee.subcollections ?? {})) {
      for (const row of rows) {
        await addDoc(collection(db, "users", uid, "giftees", gifteeRef.id, name), {
          ...row,
          userId: uid,
          ...("gifteeId" in row ? { gifteeId: gifteeRef.id } : {}),
        });
      }
    }
  }
}

async function deleteGifteeTree(uid) {
  const gifteesSnap = await getDocs(collection(db, "users", uid, "giftees"));

  for (const gifteeDoc of gifteesSnap.docs) {
    for (const name of GIFTEE_SUBCOLLECTIONS) {
      const snap = await getDocs(
        collection(db, "users", uid, "giftees", gifteeDoc.id, name),
      );
      for (const row of snap.docs) {
        await deleteDoc(row.ref);
      }
    }
    await deleteDoc(gifteeDoc.ref);
  }
}

/**
 * "Exit trial" — removes the guest's documents and the anonymous user itself.
 * Best effort: on failure the session is still ended and `cleanupAbandonedGuests`
 * sweeps whatever is left.
 */
export async function discardGuestSession() {
  const user = auth.currentUser;
  if (!isGuestUser(user)) return false;

  const uid = user.uid;
  try {
    await deleteGifteeTree(uid);
    await deleteDoc(doc(db, "users", uid));
  } catch (error) {
    console.error("discardGuestSession: could not delete guest data:", error);
  }

  try {
    await user.delete();
    return true;
  } catch (error) {
    console.error("discardGuestSession: could not delete guest user:", error);
    await signOut(auth);
    return false;
  }
}

/* ── Migration into a pre-existing account ──────────────────────────────── */

/** `{ fromUid, tree }` captured before the Firebase user is swapped. */
let pendingGuestTree = null;

/**
 * Snapshots the current guest's giftees so they can be re-created under whichever
 * account the visitor signs into next. Call this *before* signing in.
 */
export async function captureGuestTreeForMigration() {
  const user = auth.currentUser;
  if (!isGuestUser(user)) return false;

  try {
    const tree = await readGifteeTree(user.uid);
    if (!tree.giftees.length) {
      pendingGuestTree = null;
      return false;
    }
    pendingGuestTree = { fromUid: user.uid, tree };
    return true;
  } catch (error) {
    console.error("captureGuestTreeForMigration:", error);
    pendingGuestTree = null;
    return false;
  }
}

export function clearPendingGuestTree() {
  pendingGuestTree = null;
}

export function hasPendingGuestTree() {
  return pendingGuestTree != null;
}

/**
 * Recovery for a login attempt that ended signed out instead of in an account
 * (e.g. the phone number had no account, so `PhoneAuth` signed out): the previous
 * anonymous session cannot be resumed, so start a fresh one and restore the trial
 * into it. The orphaned guest is swept by `cleanupAbandonedGuests`.
 */
export async function restoreGuestSessionFromPending() {
  const pending = pendingGuestTree;
  if (!pending || auth.currentUser) return false;

  pendingGuestTree = null;
  try {
    const user = await startGuestSession();
    await writeGifteeTreeForUser(user.uid, pending.tree);
    return true;
  } catch (error) {
    console.error("restoreGuestSessionFromPending:", error);
    return false;
  }
}

/** Writes a captured trial tree into `uid` once the visitor is signed in. */
export async function flushPendingGuestTree(uid) {
  const pending = pendingGuestTree;
  if (!pending || !uid || pending.fromUid === uid) return false;

  pendingGuestTree = null;
  try {
    await writeGifteeTreeForUser(uid, pending.tree);
    return true;
  } catch (error) {
    console.error("flushPendingGuestTree:", error);
    return false;
  }
}
